import { Component, DestroyRef, Input, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { TransactionAuthorizationComponent, UserMessagesComponent } from '@zero/shared/ui-web-components';
import { ButtonComponent } from '@zero/shared/ui-web-design-system';
import { AuthPayloadTicket, AuthorizationData, Ticket, TicketData, TransactionAuthorizationResult } from '@zero/shared/util-models';
import { UserMessagesHolderService } from '@zero/shared/util-web-app-services';
import { TicketFacade } from '@zero/tickets/domain';

@Component({
  selector: 'self-service-vvt-reset',
  templateUrl: './vvt-reset.component.html',
  providers: [UserMessagesHolderService],
  imports: [FormsModule, TransactionAuthorizationComponent, UserMessagesComponent, ButtonComponent],
})
export class VvtResetComponent {
  @Input() public customerId: string;

  public dialogMode: 'initial' | 'edit' | 'verify' = 'initial';
  public editData = {
    selected: 'both',
  };
  public dataToAuthorize?: AuthorizationData<AuthPayloadTicket>;
  public preconditionFailed = false;

  #ticket?: Ticket;
  readonly #ticketFacade = inject(TicketFacade);
  readonly #messageService = inject(UserMessagesHolderService);
  readonly #destroyRef = inject(DestroyRef);

  public onSubmit(f: NgForm): void {
    if (this.dialogMode === 'edit') {
      this.#startVvtReset(f);
    }
  }

  #startVvtReset(f: NgForm): void {
    if (f.valid) {
      this.#messageService.clearAllMessages();
      this.#ticket = new Ticket();
      this.#ticket.customerId = this.customerId;
      this.#ticket.type = 'VVTRESET_VIATAN';

      this.#ticket.ticketData = new TicketData();
      this.#ticket.ticketData.additionalProperties = this.#prepareEditData();
      this.dataToAuthorize = new AuthorizationData<AuthPayloadTicket>(new AuthPayloadTicket(this.#ticket));
      this.dialogMode = 'verify';
    }
  }

  #prepareEditData(): any {
    const ret = {};
    ret[TicketData.ATTR_VVTRESET_STOCKS] = ['both', 'stocks'].indexOf(this.editData.selected) > -1;
    ret[TicketData.ATTR_VVTRESET_OTHER] = ['both', 'other'].indexOf(this.editData.selected) > -1;

    return ret;
  }

  public authorizedTransactionResult(result: TransactionAuthorizationResult) {
    if (result.success) {
      this.#messageService.clearAllMessages();
      this.#messageService.addInfoMessage(
        'Die Autorisierung war erfolgreich. ' +
          'Die Verlustbescheinigung wird zusammen mit der Jahressteuerbescheinigung erstellt und in der Regel ' +
          'im März/April des Folgejahres im Posteingang und im ' +
          'Kundenportal der Baader Bank bereitgestellt.',
        true,
      );
      this.dataToAuthorize = undefined;
    } else if (result.reason === 'CanceledByUser') {
      this.dialogMode = 'initial';
      this.dataToAuthorize = undefined;
    }
  }

  public startEdit() {
    this.#messageService.clearAllMessages();
    if (!this.#isBeforeDeadline()) {
      this.preconditionFailed = true;
      this.#messageService.addErrorMessage('Eine Beantragung ist nur bis zum 15.12. eines Jahres möglich.');
      this.dialogMode = 'edit';
      return;
    }
    this.#ticketFacade.loadTickets(true, 0, 999, 'ALL');
    this.#ticketFacade.tickets$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe((tickets) => {
      if (tickets) {
        const currentYearVVTResetTicketsCount = tickets
          .filter((t) => t.type === 'VVTRESET_VIATAN')
          .filter((t) => this.#isDateInCurrentYear(t.created)).length;

        if (currentYearVVTResetTicketsCount > 0) {
          this.#messageService.addErrorMessage(
            'Du hast in diesem Jahr bereits einen Antrag auf Ausstellung einer Verlustbescheinigung gestellt.',
          );
          this.preconditionFailed = true;
        }
        this.dialogMode = 'edit';
      } else {
        this.#messageService.addErrorMessage('Es ist ein Fehler aufgetreten, bitte versuche es später noch einmal.');
      }
    });
  }

  #isBeforeDeadline(): boolean {
    const nowYearUTC = new Date().getUTCFullYear();
    const deadlineUTC = Date.UTC(nowYearUTC, 11, 15, 22, 59, 59);
    return Date.now() < deadlineUTC;
  }

  #isDateInCurrentYear(dateToCheck: Date | string): boolean {
    if (typeof dateToCheck === 'string') {
      return new Date(Date.parse(dateToCheck)).getUTCFullYear() === new Date().getUTCFullYear();
    } else if (typeof dateToCheck !== 'undefined') {
      return dateToCheck.getUTCFullYear() === new Date().getUTCFullYear();
    } else {
      return false;
    }
  }
}
